import { useCallback, useRef } from "react";

const usePrefetch = (
  getSitesForItems,
  siteList,
  getProjectList,
  projectList,
  getWafOverview,
  wafOverview
) => {
  const prefetchedRef = useRef(new Set());
  const inProgressRef = useRef(new Set());

  const prefetchSites = useCallback(async () => {
    if (prefetchedRef.current.has("sites")) return;
    if (inProgressRef.current.has("sites")) return;
    if (siteList === null && getSitesForItems) {
      inProgressRef.current.add("sites");
      try {
        await getSitesForItems();
        prefetchedRef.current.add("sites");
      } catch (err) {
        console.warn("Prefetch sites failed, will retry on next hover:", err);
      } finally {
        inProgressRef.current.delete("sites");
      }
    }
  }, [getSitesForItems, siteList]);

  const prefetchProjects = useCallback(async () => {
    if (prefetchedRef.current.has("projects")) return;
    if (inProgressRef.current.has("projects")) return;
    if (projectList === null && getProjectList) {
      inProgressRef.current.add("projects");
      try {
        await getProjectList();
        prefetchedRef.current.add("projects");
      } catch (err) {
        console.warn("Prefetch projects failed, will retry on next hover:", err);
      } finally {
        inProgressRef.current.delete("projects");
      }
    }
  }, [getProjectList, projectList]);

  const prefetchWafOverview = useCallback(async () => {
    if (prefetchedRef.current.has("wafOverview")) return;
    if (inProgressRef.current.has("wafOverview")) return;
    if (wafOverview === null && getWafOverview) {
      inProgressRef.current.add("wafOverview");
      try {
        await getWafOverview();
        prefetchedRef.current.add("wafOverview");
      } catch (err) {
        console.warn("Prefetch WAF overview failed, will retry on next hover:", err);
      } finally {
        inProgressRef.current.delete("wafOverview");
      }
    }
  }, [getWafOverview, wafOverview]);

  const prefetchOverview = useCallback(() => {
    prefetchProjects();
  }, [prefetchProjects]);

  const prefetchSitesPage = useCallback(() => {
    prefetchSites();
    prefetchProjects();
  }, [prefetchSites, prefetchProjects]);

  const prefetchServices = useCallback(() => {
    prefetchSites();
    prefetchWafOverview();
  }, [prefetchSites, prefetchWafOverview]);

  const prefetchDetection = useCallback(() => {
    prefetchSites();
  }, [prefetchSites]);

  const prefetchCerts = useCallback(() => {
    prefetchSites();
  }, [prefetchSites]);

  const prefetchFindings = useCallback(() => {
    prefetchSites();
  }, [prefetchSites]);

  const prefetchSettings = useCallback(() => {
    prefetchProjects();
  }, [prefetchProjects]);

  const clearCache = useCallback((key) => {
    if (key) {
      prefetchedRef.current.delete(key);
    } else {
      prefetchedRef.current.clear();
    }
  }, []);

  return {
    prefetchOverview,
    prefetchSitesPage,
    prefetchServices,
    prefetchDetection,
    prefetchCerts,
    prefetchFindings,
    prefetchSettings,
    clearCache,
  };
};

export default usePrefetch;
